<script setup>
import { ref, computed, watch } from 'vue';
import HeaderActions from 'widget/components/HeaderActions.vue';
import { CsText } from 'dashboard/components/ui/commslayer';
import { useStore } from 'widget/composables/store';
import { animateEnter } from 'widget/composables/useEnterAnimation';

const STAGGER_DELAY = 20;

defineProps({
  avatarUrl: {
    type: String,
    default: '',
  },
  introHeading: {
    type: String,
    default: '',
  },
  introBody: {
    type: String,
    default: '',
  },
  showPopoutButton: {
    type: Boolean,
    default: false,
  },
});

// Store
const store = useStore();

// Animation refs
const avatarRef = ref(null);
const headingRef = ref(null);
const bodyRef = ref(null);

// Watch for widget open to trigger animations
const isWidgetOpen = computed(() => store.getters['appConfig/getIsWidgetOpen']);

const runEnterAnimations = () => {
  let delay = 0;

  if (avatarRef.value) {
    animateEnter(avatarRef.value, delay);
    delay += STAGGER_DELAY;
  }

  if (headingRef.value) {
    animateEnter(headingRef.value, delay);
    delay += STAGGER_DELAY;
  }

  if (bodyRef.value) {
    animateEnter(bodyRef.value, delay);
  }
};

watch(
  isWidgetOpen,
  (newVal, oldVal) => {
    if (newVal && !oldVal) {
      runEnterAnimations();
    }
  },
  { immediate: true }
);
</script>

<template>
  <header
    class="header-expanded pt-4 pb-4 px-4 relative box-border w-full bg-transparent"
  >
    <div
      class="flex items-start"
      :class="[avatarUrl ? 'justify-between' : 'justify-end']"
    >
      <img
        v-if="avatarUrl"
        ref="avatarRef"
        class="h-16 w-16 mb-4 rounded-full object-cover"
        :src="avatarUrl"
        alt="Avatar"
      />
      <HeaderActions
        :show-popout-button="showPopoutButton"
        :show-end-conversation-button="false"
      />
    </div>

    <div ref="headingRef">
      <CsText
        variant="heading-lg"
        weight="medium"
        class="!subpixel-antialiased"
      >
        <span v-dompurify-html="introHeading" />
      </CsText>
    </div>

    <div ref="bodyRef">
      <CsText variant-color="secondary" class="mt-2">
        <span v-dompurify-html="introBody" />
      </CsText>
    </div>
  </header>
</template>
